import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createStyles, makeStyles, useTheme } from '@material-ui/core/styles';
import NavAppbar from '../nav-top/NavAppbar';
import List from '@material-ui/core/List';
import EmailItem from './EmailItem';
import { getNoEmailText } from './utils';
import { getInboxPermissionLevel, isCompanyAccessUser } from './inbox-permission-utils';
import { getSecurityGroupsState } from '../../features/security/selectors';
import { descendingComparator } from '../document-index/DocumentIndexUtils';
import Hidden from '@material-ui/core/Hidden';
import { MenuItem, Select, TextField, Tooltip, useMediaQuery } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import Typography from '@material-ui/core/Typography';
import { useParams } from 'react-router';
import {
  copyToClipboard,
  downloadFileFromLink,
  generateUniqueId,
  highlightInIframe,
  isPdf,
  OptionalProjectMatchParams,
} from '../../scripts/utils';
import {
  EmailSearchResult,
  InboxPermissionLevel,
  UserNotificationWithEmail,
} from '../../api-client/autogenerated';
import PostalMime from 'postal-mime';
import {
  getEmailById,
  getEmailSourceFromEmailId,
  getMyNotificationsWithEmails,
} from '../../models/api/email';
import EmailFilterDialog from './EmailFilterDialog';
import NotificationEmailItem from './NotificationEmailItem';
import CircularLoader from '../loader/CircularLoader';
import InfiniteScroll from 'react-infinite-scroll-component';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { getUserState } from '../../features/user/selectors';
import { getCurrentProjectUser, getProjectState } from '../../features/project/selectors';
import { ARCHIVED_PROJECT_TOOLTIP, isProjectArchived } from '../../scripts/permission-utils';
import {
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  Assignment,
  Delete,
  DoneAll,
  InsertDriveFileOutlined,
  SelectAll,
} from '@material-ui/icons';
import PDFIcon from '../icons/PDF-icon';
import DownloadIcon from '../icons/Download-icon';
import EyeIcon from '@material-ui/icons/Visibility';
import IconButton from '@material-ui/core/IconButton';
import _ from 'lodash';
import {
  deleteReceivedEmailsById,
  deleteUserNotificationsByIds,
  markManyNotificationsAsRead,
  restoreReceivedEmail,
} from '../../models/api/notifications';
import { useLocation } from 'react-router-dom';
import { addSnackbar } from '../../features/snackbar/actions';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { searchEmails } from '../../models/api/search';
import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      padding: 24,
      height: '100vh',
      overflow: 'hidden',
    },
    emailListContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minWidth: 480,
      maxWidth: 600,
      flex: '0 0 clamp(480px, 30vw, 600px)',
      flexShrink: 0,
    },
    emailListContainerMobile: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    emailList: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflowY: 'auto',
      float: 'left',
      width: '100%',
    },
    emailListMobile: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      overflowY: 'auto',
    },
    emailPreviewContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
      height: '100%',
      borderLeft: '8px solid #E5EEFE',
      borderTopLeftRadius: 8,
    },
    emailPreview: {
      width: '100%',
      height: '100%',
      overflowY: 'scroll',
      border: 'none',
      borderBottomRightRadius: 8,
    },
    mobileEmailPreviewContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      marginTop: 40,
    },
    userInfoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '34px',
      lineHeight: '40px',
      letterSpacing: '-0.6px',
      color: '#0947B9', // Blue / Blue 700 Brand Dark
      marginBottom: 8,
    },
    deleteButton: {
      backgroundColor: '#ED3F26',
      '&:hover': {
        backgroundColor: '#FF5D45',
      },
    },
  }),
);

export enum EmailLogType {
  CORRESPONDENCE_LOG,
  MY_NOTIFICATIONS,
}

interface Attachment {
  id: string;
  name: string;
  url: string;
  buffer?: ArrayBuffer;
}

type Props = {
  type: EmailLogType;
};

const PAGE_SIZE = 20;

export default function CorrespondenceLog(props: Props) {
  const { type } = props;
  const theme = useTheme();
  const classes = useStyles();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const xsDown = useMediaQuery(theme.breakpoints.down('xs'));
  const { projectId } = useParams<OptionalProjectMatchParams>();

  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);
  // Deleting or restoring an email edits the project's correspondence log, which an archived
  // project keeps as a record.
  const isArchivedProject = isProjectArchived(project);
  const projectUser = useSelector(getCurrentProjectUser);
  const securityGroups = useSelector(getSecurityGroupsState);
  const companyViewSecurityGroup = useMemo(
    () => securityGroups.find((sg) => sg.id === project?.subscriber?.companyViewSecurityGroupId),
    [securityGroups, project?.subscriber?.companyViewSecurityGroupId],
  );

  const { id: queryEmailId, query, createdAfterDate, createdBeforeDate } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    const query = params.get('query');
    const createdAfterDate = params.get('after');
    const createdBeforeDate = params.get('before');
    return { id: id?.trim() || null, query, createdAfterDate, createdBeforeDate };
  }, [location.search]);

  const [listLoading, setListLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(true);

  const [searchQuery, setSearchQuery] = useState(query ?? '');
  const [activeSearchQuery, setActiveSearchQuery] = useState(query ?? '');
  const [searchedTerm, setSearchedTerm] = useState('');

  const [userPermissionLevel, setUserPermissionLevel] = useState<InboxPermissionLevel>();
  const [
    selectedPermissionLevel,
    setSelectedPermissionLevel,
  ] = useState<InboxPermissionLevel | null>(null);

  const [emails, setEmails] = useState<EmailSearchResult[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailSearchResult | null>(null);
  const [selectedEmailBody, setSelectedEmailBody] = useState<string>();
  const [selectedEmailAttachments, setSelectedEmailAttachments] = useState<Attachment[]>([]);

  const [notifications, setNotifications] = useState<UserNotificationWithEmail[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>();
  const [
    selectedNotification,
    setSelectedNotification,
  ] = useState<UserNotificationWithEmail | null>(null);

  const selectedIndicesNotifications = selectedIndices.map((index) => notifications[index]);
  const allSelectedNotificationsAreRead = _.compact(selectedIndicesNotifications).every(
    (n) => !!n.isRead,
  );

  const listRef = useRef<HTMLUListElement | null>(null);
  const infiniteScrollRef = useRef<InfiniteScroll | null>(null);
  const filterRef = useRef(null);
  const autoOpenedEmailIdRef = useRef<string | null>(null);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterHasAttachment, setFilterHasAttachment] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState<Dayjs | null>(
    createdAfterDate ? dayjs(createdAfterDate) : null,
  );
  const [filterToDate, setFilterToDate] = useState<Dayjs | null>(
    createdBeforeDate ? dayjs(createdBeforeDate) : null,
  );
  const [filterIsUnread, setFilterIsUnread] = useState(true);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const didMountRef = useRef(false);

  const [selectedEmailIds, setSelectedEmailIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    clearFilters();
    setSelectedEmail(null);
    setSelectedNotification(null);
    setSelectedEmailBody(undefined);
    setSelectedEmailAttachments([]);
    setSelectedIndices([]);
    setPage(0);
    setLastSelectedIndex(undefined);
    setFilterIsUnread(true);
  }, [type]);

  const clearFilters = () => {
    setFilterFrom('');
    setFilterTo('');
    setFilterSubject('');
    setFilterHasAttachment(false);
    setFilterFromDate(null);
    setFilterToDate(null);
  };

  const getUserFriendlyPermissionLevel = () => {
    switch (userPermissionLevel) {
      case InboxPermissionLevel.NUMBER_0:
        return 'Personal';

      case InboxPermissionLevel.NUMBER_2:
        return 'Company';

      case InboxPermissionLevel.NUMBER_3:
        return 'Everyone';

      case InboxPermissionLevel.NUMBER_4:
        return 'Admin';

      default:
        return '';
    }
  };

  const getEmptyListText = () => {
    switch (userPermissionLevel) {
      case InboxPermissionLevel.NUMBER_0:
        return ' for you';

      case InboxPermissionLevel.NUMBER_2:
        return ' for your company';

      default:
      case InboxPermissionLevel.NUMBER_4:
        return '';
    }
  };

  const getFilterFromDate = () => {
    if (!filterFromDate || !filterFromDate.isValid()) return undefined;

    return filterFromDate
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0)
      .toISOString();
  };

  const getFilterToDate = () => {
    if (!filterToDate || !filterToDate.isValid()) return undefined;

    return filterToDate
      .set('hour', 23)
      .set('minute', 59)
      .set('second', 59)
      .set('millisecond', 999)
      .toISOString();
  };

  const getEmailFromHeader = () => {
    if (selectedEmail) return `${selectedEmail?.senderName} <${selectedEmail?.senderAddress}>`;
    if (selectedNotification) return selectedNotification.email.Source;
  };

  const getEmailToHeader = () => {
    if (selectedEmail)
      return `${selectedEmail?.recipientName} <${selectedEmail?.recipientAddress}>`;
    if (selectedNotification) return selectedNotification.email.Destination?.ToAddresses?.[0];
  };

  const getEmailSubjectHeader = () => {
    if (selectedEmail) return selectedEmail.subject;
    if (selectedNotification) return selectedNotification.email.Message?.Subject?.Data;
  };

  const getEmailDateHeader = () => {
    if (selectedEmail) return dayjs(selectedEmail.sendDate).format('MM/DD/YYYY HH:mm A z');
    if (selectedNotification)
      return dayjs(selectedNotification?.createdOn).format('MM/DD/YYYY HH:mm A z');
  };

  const markNotificationReadLocally = (id: string) => {
    const localNotificationIndex = notifications.findIndex((n) => n.id === id);

    if (localNotificationIndex !== -1) {
      setNotifications((prev) => [
        ...prev.slice(0, localNotificationIndex),
        { ...prev[localNotificationIndex], isRead: !allSelectedNotificationsAreRead },
        ...prev.slice(localNotificationIndex + 1),
      ]);
    }
  };

  const markNotificationRead = async (userNotificationId: string) => {
    await markManyNotificationsAsRead(
      user.id,
      [userNotificationId],
      !allSelectedNotificationsAreRead,
    );
    markNotificationReadLocally(userNotificationId);
  };

  const markSelectedNotificationsRead = async () => {
    const userNotificationIds = selectedIndices.map((index) => notifications[index].id);
    await markManyNotificationsAsRead(
      user.id,
      userNotificationIds,
      !allSelectedNotificationsAreRead,
    );
    userNotificationIds.map(markNotificationReadLocally);
  };

  const deleteSelectedNotifications = async () => {
    const userNotificationIds = selectedIndices.map((index) => notifications[index].id);
    await deleteUserNotificationsByIds(userNotificationIds);
    fetchEmails();
  };

  const handleDeleteCorrespondenceLogItems = async () => {
    if (projectId) {
      const shouldDelete = window.confirm(
        `Are you sure you want to delete ${selectedEmailIds.length} selected email item${
          selectedEmailIds.length > 1 ? 's' : ''
        }?`,
      );

      if (shouldDelete) {
        try {
          setIsDeleting(true);
          setListLoading(true);
          await deleteReceivedEmailsById(projectId, selectedEmailIds);
          dispatch(
            addSnackbar({
              id: Date.now(),
              message: `Email items deleted successfully.`,
              severity: 'success',
            }),
          );
          fetchEmails();
        } catch (error: any) {
          setListLoading(false);
          dispatch(
            addSnackbar({
              id: Date.now(),
              message: `Error deleting email items. ${error.message}`,
              severity: 'error',
            }),
          );
        } finally {
          setIsDeleting(false);
        }
      }
    }
  };

  const handleRestoreCorrespondenceLogItem = async () => {
    if (user.isSiteAdmin && selectedEmail) {
      const shouldRestore = window.confirm(
        'Are you sure you want to restore the selected email item?',
      );

      if (shouldRestore) {
        try {
          setIsRestoring(true);
          setListLoading(true);
          await restoreReceivedEmail(selectedEmail.id);
          setSelectedEmail(null);
          setSelectedEmailBody('');
          dispatch(
            addSnackbar({
              id: Date.now(),
              message: `Email item restored successfully.`,
              severity: 'success',
            }),
          );
          fetchEmails();
        } catch (error: any) {
          setListLoading(false);
          dispatch(
            addSnackbar({
              id: Date.now(),
              message: `Error restoring email item. ${error.message}`,
              severity: 'error',
            }),
          );
        } finally {
          setIsRestoring(false);
        }
      }
    }
  };

  const getDeleteTooltipMessage = () => {
    if (isArchivedProject) {
      return ARCHIVED_PROJECT_TOOLTIP;
    }
    if (
      userPermissionLevel !== InboxPermissionLevel.NUMBER_4 &&
      selectedPermissionLevel !== InboxPermissionLevel.NUMBER_0
    ) {
      return 'You are permitted to delete only the email items sent to/from you.';
    }
    if (selectedEmailIds.length === 0) {
      return 'Click on a checkbox to select an email item to delete.';
    }
    return '';
  };

  const selectAll = () => {
    setSelectedIndices(
      _.range((type === EmailLogType.MY_NOTIFICATIONS ? notifications : emails).length),
    );
  };

  const closeEmailPreview = () => {
    setSelectedEmail(null);
    setSelectedNotification(null);
    setSelectedEmailBody(undefined);
  };

  const fetchNextPage = async () => {
    if (type === EmailLogType.CORRESPONDENCE_LOG) {
      if (!projectId) return;
      const data = await searchEmails(
        projectId,
        {
          searchTerm: activeSearchQuery,
          fromEmail: filterFrom,
          toOrCcEmail: filterTo,
          hasAttachment: filterHasAttachment,
          subject: filterSubject,
          createdAfterDate: getFilterFromDate(),
          createdBeforeDate: getFilterToDate(),
          permissionLevel: selectedPermissionLevel ?? undefined,
          excludeHidden: false,
        },
        page + 1,
        PAGE_SIZE,
      );
      setEmails((prev) => [...prev, ...data.emails.results]);
      setPage((prev) => prev + 1);
      setHasMoreResults(data.emails.results.length === PAGE_SIZE);
    } else {
      const data = await getMyNotificationsWithEmails(
        {
          projectId,
          fromDate: getFilterFromDate(),
          toDate: getFilterToDate(),
          isUnread: filterIsUnread,
        },
        { page: page + 1, pageSize: PAGE_SIZE, orderByDirection: sortOrder },
      );
      setNotifications((prev) => [...prev, ...data]);
      setPage((prev) => prev + 1);
      setHasMoreResults(data.length === PAGE_SIZE);
    }
  };

  const fetchEmails = async () => {
    setListLoading(true);
    setSelectedIndices([]);
    if (type === EmailLogType.CORRESPONDENCE_LOG) {
      // Cleared before returning: the loader was switched on above, and nothing else turns it
      // off on this path, so bailing out here used to leave the list spinning for good.
      if (!projectId) {
        setListLoading(false);
        return;
      }

      try {
        setSelectedEmailIds([]);
        const data = await searchEmails(
          projectId,
          {
            searchTerm: activeSearchQuery,
            fromEmail: filterFrom,
            toOrCcEmail: filterTo,
            hasAttachment: filterHasAttachment,
            subject: filterSubject,
            createdAfterDate: getFilterFromDate(),
            createdBeforeDate: getFilterToDate(),
            permissionLevel: selectedPermissionLevel ?? undefined,
            excludeHidden: false,
          },
          0,
          PAGE_SIZE,
        );
        setPage(0);
        setEmails(data.emails.results);
        setHasMoreResults(data.emails.results.length === PAGE_SIZE);
        if (data.permission !== null) {
          setSelectedPermissionLevel(data.permission);
          if (userPermissionLevel === undefined) {
            setUserPermissionLevel(data.permission);
          }
        }
      } finally {
        setListLoading(false);
      }
    } else {
      try {
        const data = await getMyNotificationsWithEmails(
          {
            projectId,
            fromDate: getFilterFromDate(),
            toDate: getFilterToDate(),
            isUnread: filterIsUnread,
          },
          { page: 0, pageSize: PAGE_SIZE, orderByDirection: sortOrder },
        );
        setPage(0);
        setNotifications(data);
        setHasMoreResults(data.length === PAGE_SIZE);
      } finally {
        setListLoading(false);
      }
    }
  };

  useEffect(() => {
    if (
      user &&
      project &&
      (projectUser ||
        user.isSiteAdmin ||
        user.adminOfSubscriberId === project.subscriberId ||
        isCompanyAccessUser(user, project))
    ) {
      setSelectedPermissionLevel(
        getInboxPermissionLevel(user, project, projectUser, companyViewSecurityGroup),
      );
    }
  }, [user, project, projectUser, companyViewSecurityGroup]);

  useEffect(() => {
    setSelectedIndices([]);

    if (type === EmailLogType.MY_NOTIFICATIONS || selectedPermissionLevel !== null) {
      fetchEmails().then(async () => {
        const scrollHeight = listRef.current?.scrollHeight || 0;
        const clientHeight = listRef.current?.clientHeight || 0;
        if (!(scrollHeight > clientHeight)) {
          infiniteScrollRef.current?.setState({ showLoader: true });
          await fetchNextPage();
          infiniteScrollRef.current?.setState({ showLoader: false });
        }
      });
    }
  }, [
    type,
    projectId,
    activeSearchQuery,
    filterFrom,
    filterTo,
    filterSubject,
    filterHasAttachment,
    filterFromDate,
    filterToDate,
    filterIsUnread,
    sortOrder,
    selectedPermissionLevel,
  ]);

  useEffect(() => {
    if (type !== EmailLogType.CORRESPONDENCE_LOG) return;
    if (!projectId) return;
    if (!queryEmailId) return;

    if (autoOpenedEmailIdRef.current === queryEmailId) return;

    let cancelled = false;

    (async () => {
      try {
        const email = await getEmailById(queryEmailId, query || '');
        if (cancelled) return;

        if (!email) return;

        setSelectedNotification(null);
        setSelectedEmail(email);
        setSearchedTerm(query || '');
        autoOpenedEmailIdRef.current = queryEmailId;
      } catch (e) {
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: 'Something went wrong while opening the email.',
            severity: 'error',
          }),
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [type, projectId, queryEmailId]);

  useEffect(() => {
    setSelectedEmailAttachments([]);
    if (selectedEmail) {
      setPreviewLoading(true);
      getEmailSourceFromEmailId(selectedEmail.id)
        .then(async (data) => {
          const parser = new PostalMime();
          const email = await parser.parse(data);
          setSelectedEmailBody(email.html || email.text);
          const attachments = email.attachments
            .filter((a) => a.disposition === 'attachment')
            .map((a) => {
              const buffer = (a.content as unknown) as ArrayBuffer;
              return {
                id: generateUniqueId(),
                name: a.filename ?? 'attachment',
                url: URL.createObjectURL(new Blob([buffer])),
                buffer,
              };
            });
          setSelectedEmailAttachments(attachments);
        })
        // Without this, a failed fetch or an unparseable message left the preview spinning
        // forever with no indication that anything had gone wrong.
        .catch(() => setSelectedEmailBody(undefined))
        .finally(() => setPreviewLoading(false));
    }
  }, [selectedEmail]);

  const viewPdfAttachment = (fileName: string, buffer: ArrayBuffer) => {
    const channel = new BroadcastChannel('pdf_attachment');

    const params = new URLSearchParams({ buffer: 'true', fileName: encodeURIComponent(fileName) });
    if (activeSearchQuery) params.set('query', activeSearchQuery);

    const newWindow = window.open(
      `${window.location.origin}/main/pdf/view?${params.toString()}`,
      '_blank',
      'noopener,noreferrer',
    );

    if (newWindow) newWindow.opener = null;

    channel.onmessage = (event) => {
      if (event.data === 'ready') {
        channel.postMessage({ buffer, fileName });
        channel.close();
      }
    };
  };

  useEffect(() => {
    if (selectedNotification) {
      setSelectedEmailBody(selectedNotification.email.Message?.Body?.Html?.Data);
    }
  }, [selectedNotification]);

  const filterCount = [
    filterFrom,
    filterTo,
    filterSubject,
    filterHasAttachment,
    filterFromDate,
    filterToDate,
  ]
    .map((v) => (!!v ? 1 : 0))
    .reduce<number>((a, b) => a + b, 0);

  const notificationOnClick = (
    event: React.MouseEvent<HTMLLIElement>,
    notification: UserNotificationWithEmail,
    index: number,
  ) => {
    event.stopPropagation();
    if (event.shiftKey) {
      if (lastSelectedIndex !== undefined) {
        setLastSelectedIndex(index);
        setSelectedIndices(
          _.range(lastSelectedIndex, index > lastSelectedIndex ? index + 1 : index - 1),
        );
      } else {
        setSelectedIndices([index]);
      }
    } else if (event.ctrlKey) {
      setLastSelectedIndex(index);
      if (selectedIndices.includes(index)) {
        setSelectedIndices((prev) => prev.filter((v) => v !== index));
      } else {
        setSelectedIndices((prev) => [...prev, index]);
      }
    } else {
      setLastSelectedIndex(index);
      setSelectedIndices([index]);
    }

    if (event.detail > 1 && event.buttons === 0) {
      setSelectedNotification(notification);
      if (!notification.isRead) {
        markNotificationRead(notification.id);
      }
    }
  };

  const getEmails = () => {
    if (type === EmailLogType.CORRESPONDENCE_LOG) {
      if (emails.length === 0) {
        if (filterCount > 0) {
          return (
            <Typography align="center" style={{ marginTop: 16 }}>
              No emails found with the current filters.
            </Typography>
          );
        }
        return (
          <Typography align="center" style={{ marginTop: 16 }}>
            No emails found{getEmptyListText()}.
          </Typography>
        );
      }
      return emails
        .filter((e) =>
          selectedPermissionLevel !== InboxPermissionLevel.NUMBER_4 ? !e.isHidden : true,
        )
        .sort((a, b) => descendingComparator(a, b, 'sendDate'))
        .map((e, index, array) => (
          <EmailItem
            key={e.id}
            email={e}
            onClick={() => {
              setSelectedEmail(e);
              setSearchedTerm(activeSearchQuery);
            }}
            isSelected={selectedEmail?.id === e.id}
            isLastItem={index === array.length - 1}
            showCheckbox={
              selectedPermissionLevel === InboxPermissionLevel.NUMBER_0 ||
              userPermissionLevel === InboxPermissionLevel.NUMBER_4
            }
            isChecked={selectedEmailIds.includes(e.id)}
            onCheckboxChange={(checked) => {
              setSelectedEmailIds((prev) =>
                checked ? [...prev, e.id] : prev.filter((id) => id !== e.id),
              );
            }}
            isHidden={selectedPermissionLevel === InboxPermissionLevel.NUMBER_4 && e.isHidden!}
          />
        ));
    } else {
      if (notifications.length === 0)
        return <Typography align="center">No notifications.</Typography>;
      return notifications.map((n, index) => (
        <NotificationEmailItem
          key={n.id}
          notificationWithEmail={n}
          onClick={(event) => notificationOnClick(event, n, index)}
          isSelected={selectedIndices.includes(index)}
        />
      ));
    }
  };

  const attachmentsContent = (attachments: Attachment[], isRelevantAttachments?: boolean) => {
    return (
      <div
        style={{
          width: '100%',
          backgroundColor: '#E5EEFE',
          padding: '0px 4px 16px 4px',
        }}
      >
        <Typography style={{ width: '100%', marginBottom: 8, fontWeight: 500 }}>
          {isRelevantAttachments ? (
            <span>
              Attachments containing "
              <span style={{ backgroundColor: 'yellow' }}>{searchedTerm}</span>":
            </span>
          ) : (
            'Attachments:'
          )}
        </Typography>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            backgroundColor: '#E5EEFE',
          }}
        >
          {attachments.map((file) => (
            <div
              key={file.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid gray',
                padding: 8,
              }}
            >
              {isPdf(file.name) ? (
                <PDFIcon style={{ fill: '#0947B9' }} />
              ) : (
                <InsertDriveFileOutlined />
              )}
              <Typography style={{ marginLeft: 8, marginRight: 8 }}>{file.name}</Typography>
              <Tooltip arrow title="Download">
                <IconButton
                  onClick={() => {
                    downloadFileFromLink(file.url, file.name);
                  }}
                  style={{ padding: 0 }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              {isPdf(file.name) && (
                <Tooltip arrow title="View">
                  <IconButton
                    onClick={() => viewPdfAttachment(file.name, file.buffer!)}
                    style={{ padding: 0 }}
                  >
                    <EyeIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const [relevantAttachments, otherAttachments] = _.partition(
    selectedEmailAttachments,
    (a) =>
      selectedEmail?.sources.includes(a.name.replace(/[^\w.\-()+]/g, '_')) ||
      selectedEmail?.sources.includes(a.name),
  );

  const emailPreviewContent = (
    <div className={smDown ? classes.mobileEmailPreviewContainer : classes.emailPreviewContainer}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',
        }}
      >
        {smDown ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={closeEmailPreview}
            style={{ marginBottom: 8 }}
          >
            Go Back
          </Button>
        ) : null}
        <div
          style={{
            width: '100%',
            padding: '8px 4px',
            backgroundColor: '#E5EEFE',
            borderTopRightRadius: 8,
          }}
        >
          <div style={{ visibility: selectedEmail || selectedNotification ? 'visible' : 'hidden' }}>
            <Typography style={{ lineHeight: '20px' }}>From: {getEmailFromHeader()}</Typography>
            <Typography style={{ lineHeight: '20px' }}>To: {getEmailToHeader()}</Typography>
            <Typography style={{ lineHeight: '20px' }}>Date: {getEmailDateHeader()}</Typography>
            <Typography style={{ lineHeight: '20px', textIndent: '-58px', paddingLeft: '58px' }}>
              Subject: {getEmailSubjectHeader()}
            </Typography>
          </div>
        </div>
        {relevantAttachments.length > 0 ? (
          <>{attachmentsContent(relevantAttachments, true)}</>
        ) : null}
        {otherAttachments.length > 0 ? <>{attachmentsContent(otherAttachments)}</> : null}
      </div>

      {!previewLoading ? (
        <iframe
          key={selectedEmail?.id ?? selectedNotification?.id ?? 'empty'}
          className={classes.emailPreview}
          title="Email Preview"
          onLoad={(event) => {
            if (type !== EmailLogType.CORRESPONDENCE_LOG) return;
            const iframe = event.target as HTMLIFrameElement;
            highlightInIframe({
              iframe,
              query: activeSearchQuery,
            });
          }}
          srcDoc={selectedEmailBody || getNoEmailText(type)}
          style={{ backgroundColor: !selectedEmailBody ? '#E5EEFE' : 'initial' }}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <CircularLoader />
        </div>
      )}
    </div>
  );

  if (smDown && (!!selectedEmail || !!selectedNotification)) {
    return <div className={classes.root}>{emailPreviewContent}</div>;
  }

  return (
    <main className={classes.root}>
      <NavAppbar />

      <div
        style={{
          display: 'flex',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <div className={smDown ? classes.emailListContainerMobile : classes.emailListContainer}>
          <Typography className={classes.title}>
            {type === EmailLogType.CORRESPONDENCE_LOG
              ? 'Correspondence Log'
              : 'My Notification Inbox'}
          </Typography>
          {type === EmailLogType.CORRESPONDENCE_LOG ? (
            <div style={{ paddingRight: 8 }}>
              <Typography className={classes.userInfoRow}>
                Project email address:{' '}
                <strong>
                  <IconButton
                    size="small"
                    style={{ padding: 0, marginRight: 4 }}
                    onClick={async () => {
                      await copyToClipboard(`${project?.emailSlug}@p.centerline.co`);
                      dispatch(
                        addSnackbar({
                          id: Date.now(),
                          message: 'Successfully copied project email address to clipboard!',
                          severity: 'success',
                        }),
                      );
                    }}
                  >
                    <Assignment />
                  </IconButton>
                  {project?.emailSlug}@p.centerline.co
                </strong>
              </Typography>
              <Typography className={classes.userInfoRow}>
                Your address: <strong>{user.email}</strong>
              </Typography>
              <Typography className={classes.userInfoRow} style={{ marginTop: 4 }}>
                Security level: <strong>{getUserFriendlyPermissionLevel()}</strong>
              </Typography>
              <div className={classes.userInfoRow} style={{ marginTop: 8 }}>
                <Typography>Displaying emails to/from:</Typography>
                <Select
                  variant="outlined"
                  disabled={userPermissionLevel === InboxPermissionLevel.NUMBER_0}
                  value={selectedPermissionLevel}
                  onChange={(event) =>
                    setSelectedPermissionLevel(event.target.value as InboxPermissionLevel)
                  }
                  style={{ width: 140, height: 32 }}
                >
                  <MenuItem key={0} value={InboxPermissionLevel.NUMBER_0}>
                    Me
                  </MenuItem>
                  {!!userPermissionLevel && userPermissionLevel >= InboxPermissionLevel.NUMBER_2 ? (
                    <MenuItem key={1} value={InboxPermissionLevel.NUMBER_2}>
                      My Company
                    </MenuItem>
                  ) : null}
                  {userPermissionLevel === InboxPermissionLevel.NUMBER_3 ||
                  userPermissionLevel === InboxPermissionLevel.NUMBER_4 ? (
                    <MenuItem key={2} value={userPermissionLevel}>
                      Everyone
                    </MenuItem>
                  ) : null}
                </Select>
              </div>
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 8px 8px 0px',
            }}
          >
            {type === EmailLogType.MY_NOTIFICATIONS ? (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: 240,
                    }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      value={filterIsUnread}
                      onChange={(event, value) => setFilterIsUnread(value)}
                    >
                      <ToggleButton
                        value={true}
                        style={{
                          color: filterIsUnread ? '#FAFAFA' : '#464546',
                          backgroundColor: filterIsUnread ? '#0947B9' : '#FAFAFA',
                          borderColor: '#0947B9',
                        }}
                      >
                        Unread
                      </ToggleButton>
                      <ToggleButton
                        value={false}
                        style={{
                          color: !filterIsUnread ? '#FAFAFA' : '#464546',
                          backgroundColor: !filterIsUnread ? '#0947B9' : '#FAFAFA',
                          borderColor: '#0947B9',
                        }}
                      >
                        All
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                      exclusive
                      value={sortOrder}
                      onChange={(event, value) => setSortOrder(value)}
                      style={{ marginLeft: 16 }}
                    >
                      <ToggleButton
                        value="desc"
                        style={{
                          color: sortOrder === 'desc' ? '#FAFAFA' : '#464546',
                          backgroundColor: sortOrder === 'desc' ? '#0947B9' : '#FAFAFA',
                          borderColor: '#0947B9',
                        }}
                      >
                        <ArrowDownward />
                      </ToggleButton>
                      <ToggleButton
                        value="asc"
                        style={{
                          color: sortOrder === 'asc' ? '#FAFAFA' : '#464546',
                          backgroundColor: sortOrder === 'asc' ? '#0947B9' : '#FAFAFA',
                          borderColor: '#0947B9',
                        }}
                      >
                        <ArrowUpward />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>
                  {!xsDown ? (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SelectAll />}
                      onClick={selectAll}
                      style={{ width: 170, whiteSpace: 'nowrap' }}
                    >
                      Select All
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={selectAll}
                      style={{ paddingLeft: 4, paddingRight: 4 }}
                    >
                      <SelectAll />
                    </Button>
                  )}
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DoneAll />}
                    disabled={selectedIndices.length === 0}
                    onClick={markSelectedNotificationsRead}
                    style={{ width: 240, whiteSpace: 'nowrap' }}
                  >
                    Mark as{' '}
                    {allSelectedNotificationsAreRead && selectedIndices.length > 0
                      ? 'Unread'
                      : 'Read'}
                  </Button>

                  {!xsDown ? (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Delete />}
                      disabled={selectedIndices.length === 0}
                      onClick={deleteSelectedNotifications}
                      className={classes.deleteButton}
                      style={{ width: 170, whiteSpace: 'nowrap' }}
                    >
                      Delete
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={selectedIndices.length === 0}
                      onClick={deleteSelectedNotifications}
                      className={classes.deleteButton}
                      style={{ paddingLeft: 4, paddingRight: 4 }}
                    >
                      <Delete />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8 }}>
                {(selectedPermissionLevel === InboxPermissionLevel.NUMBER_0 ||
                  userPermissionLevel === InboxPermissionLevel.NUMBER_4) && (
                  <Tooltip title={getDeleteTooltipMessage()}>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isDeleting || isArchivedProject || selectedEmailIds.length === 0}
                        onClick={handleDeleteCorrespondenceLogItems}
                        style={{
                          whiteSpace: 'nowrap',
                          minWidth: 36,
                          flexShrink: 0,
                          color: 'white',
                          background:
                            selectedEmailIds.length === 0 ? 'rgba(0, 0, 0, 0.34)' : '#ED3F26',
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  placeholder="Search emails"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setActiveSearchQuery(searchQuery);
                    }
                  }}
                />
                {!xsDown ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => setActiveSearchQuery(searchQuery)}
                    style={{ minWidth: 36 }}
                  >
                    <SearchIcon />
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveSearchQuery('');
                    setSearchedTerm('');
                    setSelectedEmail(null);
                    setSelectedEmailBody(undefined);
                  }}
                  style={{ whiteSpace: 'nowrap', width: 100, flexShrink: 0 }}
                >
                  Reset
                </Button>
                {selectedPermissionLevel === InboxPermissionLevel.NUMBER_4 && user.isSiteAdmin && (
                  <Button
                    disabled={
                      isRestoring || isArchivedProject || !selectedEmail || !selectedEmail.isHidden
                    }
                    variant="contained"
                    color="primary"
                    onClick={handleRestoreCorrespondenceLogItem}
                    style={{ whiteSpace: 'nowrap', width: 100, flexShrink: 0 }}
                  >
                    Restore
                  </Button>
                )}
              </div>
            )}
          </div>
          <List
            id="emailList"
            ref={listRef}
            disablePadding
            className={smDown ? classes.emailListMobile : classes.emailList}
          >
            {!listLoading ? (
              <InfiniteScroll
                next={fetchNextPage}
                ref={infiniteScrollRef}
                hasMore={hasMoreResults}
                loader={
                  <div style={{ paddingTop: 12, paddingBottom: 12 }}>
                    <CircularLoader />
                  </div>
                }
                dataLength={(page + 1) * PAGE_SIZE}
                scrollableTarget="emailList"
                scrollThreshold={0.92}
              >
                {getEmails()}
              </InfiniteScroll>
            ) : (
              <CircularLoader style={{ marginTop: 64 }} />
            )}
          </List>
        </div>

        <Hidden smDown>{emailPreviewContent}</Hidden>
      </div>
      <EmailFilterDialog
        open={filtersOpen}
        close={() => setFiltersOpen(false)}
        type={type}
        submit={(filters) => {
          setFilterTo(filters.filterTo);
          setFilterFrom(filters.filterFrom);
          setFilterSubject(filters.filterSubject);
          setFilterHasAttachment(filters.filterHasAttachment);
          setFilterFromDate(filters.filterFromDate);
          setFilterToDate(filters.filterToDate);
          setFilterIsUnread(filters.filterIsUnread);
        }}
        anchor={filterRef.current}
        filters={{
          filterTo,
          filterFrom,
          filterSubject,
          filterHasAttachment,
          filterFromDate,
          filterToDate,
          filterIsUnread,
        }}
      />
    </main>
  );
}
